/**
 * MultiplayerGameView — Modern visual game board for multiplayer.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Suit } from '@callbridge/engine';
import { useNavigate } from 'react-router-dom';
import { useGameStore, useAuthStore, useRoomStore } from '../stores';
import { emitCall, emitPlayCard, emitLeaveRoom } from '../hooks/useSocket';
import { useSoundEffects } from '../hooks/useSoundEffects';
import {
  getLocalPlayer,
  getTurnInfo,
  getPlayableCardInfos,
  getValidCallValues,
  getRelativePlayerOrder,
} from '@callbridge/ui';
import { platformSdk } from '../lib/platformSdk';
import { Card } from './Card';

// ─── Isolated Timer Components ──────────────────────────────────────────────
// These own their own countdown so the parent game board doesn't re-render
// every second just because the timer ticked.

const TURN_TOTAL_MS = 30_000;

const TurnTimerRing = React.memo(function TurnTimerRing({
  phase,
  currentTurnIndex,
}: {
  phase: string;
  currentTurnIndex: number;
}) {
  const [remaining, setRemaining] = useState(TURN_TOTAL_MS);

  useEffect(() => {
    setRemaining(TURN_TOTAL_MS);
    if (phase !== 'playing' && phase !== 'calling') return;
    const id = setInterval(() => {
      setRemaining((p) => Math.max(0, p - 1_000));
    }, 1_000);
    return () => clearInterval(id);
  }, [currentTurnIndex, phase]);

  const pct = (remaining / TURN_TOTAL_MS) * 100;
  return (
    <div className="turn-timer-ring">
      <svg viewBox="0 0 36 36">
        <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="2" />
        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth="2"
          strokeDasharray="100"
          strokeDashoffset={100 - pct}
          strokeLinecap="round"
          transform="rotate(-90 18 18)"
        />
      </svg>
    </div>
  );
});

const LowTimeIndicator = React.memo(function LowTimeIndicator({
  phase,
  currentTurnIndex,
}: {
  phase: string;
  currentTurnIndex: number;
}) {
  const [remaining, setRemaining] = useState(TURN_TOTAL_MS);

  useEffect(() => {
    setRemaining(TURN_TOTAL_MS);
    if (phase !== 'playing' && phase !== 'calling') return;
    const id = setInterval(() => {
      setRemaining((p) => Math.max(0, p - 1_000));
    }, 1_000);
    return () => clearInterval(id);
  }, [currentTurnIndex, phase]);

  if (remaining > 10_000) return null;
  return <span className="low-time-pulse"> Low time!</span>;
});

// ─── Module-level constants ───────────────────────────────────────────────────

const SUIT_SYMBOL: Record<string, string> = {
  spades: '♠',
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
};
const ALL_SUITS = ['spades', 'hearts', 'diamonds', 'clubs'];

// ─── Main Component ──────────────────────────────────────────────────────────

interface Props {
  roomCode: string;
}

const SEAT_MAP = ['bottom', 'left', 'top', 'right'] as const;
const SEAT_TO_REL: Record<string, number> = { bottom: 0, left: 1, top: 2, right: 3 };

export function MultiplayerGameView({ roomCode }: Props) {
  const navigate = useNavigate();
  const game = useGameStore();
  const user = useAuthStore((s) => s.user);
  const roomLeft = useRoomStore((s) => s.left);
  const { play: playSound } = useSoundEffects();
  const prevPhaseRef = useRef(game.phase);

  const localPlayer = getLocalPlayer(game);
  const turnInfo = getTurnInfo(game);

  // If the local player already has a card in the current trick, treat it as not their turn
  // (handles the server-update race where isMyTurn hasn't cleared yet after playing)
  const alreadyPlayedThisTrick = game.trickCards.some((tc) => tc.playerId === localPlayer?.id);
  const canPlay = turnInfo.isMyTurn && !alreadyPlayedThisTrick;
  const alreadyMadeCall =
    localPlayer !== null && localPlayer !== undefined && localPlayer.call !== null;

  // Local player's void suits — shown during calling (so you know what to bid)
  // and during playing (so you remember what you can't follow).
  const voidSuits =
    (game.phase === 'calling' || game.phase === 'playing') && game.hand.length > 0
      ? ALL_SUITS.filter((s) => !game.hand.some((c) => c.suit === s))
      : [];
  const playableCards = getPlayableCardInfos(game.hand, game.leadSuit, game.trickCards);
  const validCalls = getValidCallValues();
  const playerOrder = getRelativePlayerOrder(game);
  const [collectDirection, setCollectDirection] = useState<(typeof SEAT_MAP)[number] | null>(null);
  const [showScoreboard, setShowScoreboard] = useState(false);
  const [roundEndDismissed, setRoundEndDismissed] = useState(false);
  const [selectedCardKey, setSelectedCardKey] = useState<string | null>(null);

  const isTouchDevice = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(hover: none) and (pointer: coarse)').matches,
    [],
  );

  // Clear selected card whenever it's no longer our turn to play
  useEffect(() => {
    if (!canPlay) setSelectedCardKey(null);
  }, [canPlay]);
  // Timer countdown is now handled by TurnTimerRing / LowTimeIndicator components
  // so the parent board doesn't re-render every second.

  // Auto-dismiss error toast after 4 s so players don't have to close it manually.
  useEffect(() => {
    if (!game.error) return;
    const t = setTimeout(() => useGameStore.getState().errorSet(null), 4000);
    return () => clearTimeout(t);
  }, [game.error]);

  // When a trick is won, pause briefly to show the winning trick, then slide
  // the played cards toward the winner's seat before clearing the table.
  useEffect(() => {
    if (!game.trickWinnerId) {
      setCollectDirection(null);
      return;
    }

    const relIdx = playerOrder.findIndex((p) => p.id === game.trickWinnerId);
    const seat = SEAT_MAP[relIdx] ?? 'bottom';

    const collectTimer = setTimeout(() => setCollectDirection(seat), 900);
    const clearTimer = setTimeout(() => {
      game.trickCleared();
      setCollectDirection(null);
    }, 1400);

    return () => {
      clearTimeout(collectTimer);
      clearTimeout(clearTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.trickWinnerId]);

  // Reset the dismiss flag whenever a new roundEnd begins so the overlay
  // reappears for the next round's score screen.
  useEffect(() => {
    if (game.phase === 'roundEnd') setRoundEndDismissed(false);
  }, [game.phase]);

  useEffect(() => {
    if (game.phase === prevPhaseRef.current) return;
    prevPhaseRef.current = game.phase;
    if (game.phase === 'playing' && turnInfo.isMyTurn) playSound('yourTurn');
    if (game.phase === 'gameOver') {
      playSound('gameOver');
      if (game.winnerId === user?.id) platformSdk.happytime();
    }
    if (game.phase === 'roundEnd') playSound('trickWon');
  }, [game.phase, turnInfo.isMyTurn, playSound, game.winnerId, user?.id]);

  const handleCall = useCallback(
    async (call: number) => {
      playSound('callMade');
      if (localPlayer?.id) game.callMade(localPlayer.id, call);
      try {
        await emitCall(call);
      } catch (err) {
        game.errorSet(err instanceof Error ? err.message : 'Failed to make call');
      }
    },
    [game, localPlayer, playSound],
  );

  const handlePlayCard = useCallback(
    async (suit: string, rank: number) => {
      setSelectedCardKey(null);
      playSound('cardPlay');
      const card = { suit: suit as Suit, rank };
      if (localPlayer?.id) game.cardPlayed(localPlayer.id, card);
      try {
        await emitPlayCard(suit, rank);
      } catch (err) {
        if (localPlayer?.id) game.cardPlayUndone(localPlayer.id, card);
        game.errorSet(err instanceof Error ? err.message : 'Failed to play card');
      }
    },
    [game, localPlayer, playSound],
  );

  const handleLeaveGame = useCallback(async () => {
    try {
      await emitLeaveRoom();
    } catch {
      /* best effort */
    }
    game.reset();
    roomLeft();
    platformSdk.gameplayStop();
    await platformSdk.requestMidrollAd();
    navigate('/play/multiplayer');
  }, [game, roomLeft, navigate]);

  const handleContinueRound = useCallback(() => {
    setRoundEndDismissed(true);
  }, []);

  if (game.phase === 'idle' || !game.gameId) {
    return (
      <div className="game-board">
        <div className="game-topbar">
          <button
            className="topbar-btn"
            onClick={handleLeaveGame}
            title="Leave"
            aria-label="Leave game"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="topbar-room">Room: {roomCode}</span>
        </div>
        <div className="felt-table">
          <div className="trick-center" role="status" aria-live="polite">
            <div className="waiting-dots">
              <span />
              <span />
              <span />
            </div>
            <span className="trick-empty-text">Waiting for game to start...</span>
          </div>
        </div>
      </div>
    );
  }

  const currentTurnId = game.players[game.currentTurnIndex]?.id;
  const totalTricksDone = game.players.reduce((sum, p) => sum + p.tricksWon, 0);

  return (
    <div className="game-board">
      {/* Top Bar */}
      <div className="game-topbar">
        <button
          className="topbar-btn"
          onClick={handleLeaveGame}
          title="Leave"
          aria-label="Leave game"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="topbar-center">
          <div className="topbar-center-row">
            <span className="topbar-round">Round {game.roundNumber}</span>
            <span className="topbar-divider" />
            <span className="topbar-phase">
              {game.phase === 'playing'
                ? 'Playing'
                : game.phase === 'calling'
                  ? 'Calling'
                  : game.phase === 'roundEnd'
                    ? 'Round End'
                    : game.phase}
            </span>
          </div>
          {game.phase === 'playing' && (
            <div className="tricks-progress">
              <div className="tricks-progress-track">
                <div
                  className="tricks-progress-fill"
                  style={{ width: `${(totalTricksDone / 13) * 100}%` }}
                />
              </div>
              <div className="tricks-progress-label">
                <span>Tricks played</span>
                <span>{totalTricksDone} / 13</span>
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            className="topbar-btn"
            onClick={() => setShowScoreboard((v) => !v)}
            title="Score table"
            aria-label="Show score table"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18M9 21V9" />
            </svg>
          </button>
          <span className="topbar-room">{roomCode}</span>
        </div>
      </div>

      {game.error && (
        <div className="game-toast game-toast-error" role="alert">
          {game.error}
          <button className="toast-dismiss" onClick={() => game.errorSet(null)}>
            ×
          </button>
        </div>
      )}

      {/* Opponent Band */}
      <div className="opp-band">
        {playerOrder.slice(1).map((p) => {
          const isTurn = p.id === currentTurnId;
          return (
            <div
              key={p.id}
              className={`opp-band-cell ${isTurn ? 'opp-band-turn' : ''} ${!p.isConnected ? 'is-disconnected' : ''}`}
            >
              <div className="opp-band-avatar-wrap">
                <div
                  className={`opp-band-avatar ${p.isAI ? 'avatar-ai' : ''} ${isTurn ? 'avatar-glow' : ''}`}
                >
                  {p.isAI ? '🤖' : p.name.charAt(0).toUpperCase()}
                </div>
                {isTurn && (
                  <TurnTimerRing phase={game.phase} currentTurnIndex={game.currentTurnIndex} />
                )}
              </div>
              <div className="opp-band-info">
                <span className="opp-band-name">
                  {p.name}
                  {!p.isConnected && <span className="disconnect-dot" />}
                </span>
                {game.phase === 'calling' && (game.playerVoids[p.id]?.length ?? 0) > 0 && (
                  <span className="void-alert" title="This player has a void suit">
                    ⚠ Void
                  </span>
                )}
                <span className="opp-band-score">
                  Call: <b>{p.call ?? '—'}</b>
                  {' · '}
                  {p.tricksWon} tricks
                  {' · '}
                  <span className="opp-band-total">{p.totalScore}pt</span>
                </span>
                <div className="trick-pips">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i} className={`trick-pip${i < p.tricksWon ? ' pip-won' : ''}`} />
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Felt Table */}
      <div className="felt-table">
        {/* Desktop opponent seats — hidden on mobile (opp-band replaces them) */}
        {playerOrder.map((p, relIdx) => {
          const seat = SEAT_MAP[relIdx] ?? 'top';
          if (seat === 'bottom') return null;
          const isTurn = p.id === currentTurnId;
          return (
            <div
              key={p.id}
              className={`opponent-seat opponent-${seat} ${isTurn ? 'is-turn' : ''} ${!p.isConnected ? 'is-disconnected' : ''}`}
            >
              <div className="opponent-info">
                <div
                  className={`opponent-avatar ${isTurn ? 'avatar-glow' : ''} ${p.isAI ? 'avatar-ai' : ''}`}
                >
                  {p.isAI ? '🤖' : p.name.charAt(0).toUpperCase()}
                </div>
                <span className="opponent-name">
                  {p.name}
                  {!p.isConnected && <span className="disconnect-dot" />}
                </span>
                {game.phase === 'calling' && (game.playerVoids[p.id]?.length ?? 0) > 0 && (
                  <span className="void-alert" title="This player has a void suit">
                    ⚠ Void
                  </span>
                )}
                <span className="opponent-score">
                  Call: <b>{p.call ?? '—'}</b>{' '}
                  <span className="opponent-total">
                    {p.tricksWon} tricks · {p.totalScore}pt
                  </span>
                </span>
                <div className="trick-pips">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i} className={`trick-pip${i < p.tricksWon ? ' pip-won' : ''}`} />
                  ))}
                </div>
              </div>
              {isTurn && (
                <TurnTimerRing phase={game.phase} currentTurnIndex={game.currentTurnIndex} />
              )}
            </div>
          );
        })}

        {/* Center Trick Area */}
        <div className="trick-center">
          {/* 4-slot cross layout during playing phase */}
          {game.phase === 'playing' && (
            <div
              className={`trick-cross ${collectDirection ? `trick-collecting trick-collect-${collectDirection}` : ''}`}
            >
              {(['top', 'left', 'right', 'bot'] as const).map((gridArea) => {
                const seatName = gridArea === 'bot' ? 'bottom' : gridArea;
                const slotPlayer = playerOrder[SEAT_TO_REL[seatName] ?? 0];
                const playedCard = slotPlayer
                  ? game.trickCards.find((tc) => tc.playerId === slotPlayer.id)
                  : null;
                return (
                  <div key={gridArea} className={`trick-cross-${gridArea}`}>
                    {playedCard ? (
                      <div className="trick-slot-played">
                        <Card suit={playedCard.card.suit} rank={playedCard.card.rank} size="md" />
                      </div>
                    ) : (
                      <div className="trick-slot-empty" />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Turn pill */}
          {game.phase === 'playing' && (
            <div
              className={`turn-pill ${turnInfo.isMyTurn ? 'turn-pill-active' : 'turn-pill-waiting'}`}
            >
              {turnInfo.isMyTurn ? (
                <>
                  <span className="turn-dot" />
                  Your Turn
                  <LowTimeIndicator phase={game.phase} currentTurnIndex={game.currentTurnIndex} />
                </>
              ) : (
                <>
                  <div className="waiting-dots">
                    <span />
                    <span />
                    <span />
                  </div>
                  {turnInfo.currentPlayer?.name}...
                  <LowTimeIndicator phase={game.phase} currentTurnIndex={game.currentTurnIndex} />
                </>
              )}
            </div>
          )}
          {game.phase === 'calling' && !turnInfo.isMyTurn && (
            <div className="turn-pill turn-pill-waiting">
              <div className="waiting-dots">
                <span />
                <span />
                <span />
              </div>
              {turnInfo.currentPlayer?.name}&rsquo;s call...
            </div>
          )}
        </div>
      </div>

      {/* Call Panel */}
      {game.phase === 'calling' && turnInfo.isMyTurn && !alreadyMadeCall && (
        <div
          className="call-overlay anim-slide-up"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mp-call-title"
        >
          <div className="call-glass">
            <h3 id="mp-call-title" className="call-title">
              Make Your Call
            </h3>
            <div className="call-chips">
              {validCalls.map((call) => (
                <button key={call} className="call-chip" onClick={() => handleCall(call)}>
                  {call}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Player Hand */}
      <div className="hand-dock">
        <div className="hand-player-info">
          <div className={`hand-avatar ${canPlay ? 'avatar-glow' : ''}`}>
            {localPlayer?.name?.charAt(0).toUpperCase() ?? 'Y'}
          </div>
          <span className="hand-player-name">{localPlayer?.name ?? 'You'}</span>
          {voidSuits.map((s) => (
            <span key={s} className={`void-badge void-badge-${s}`} title={`No ${s}`}>
              {SUIT_SYMBOL[s]}
            </span>
          ))}
          <span className="hand-score">
            Call: {localPlayer?.call ?? '—'} &middot; {localPlayer?.tricksWon ?? 0} tricks &middot;{' '}
            {localPlayer?.totalScore ?? 0}pt
          </span>
        </div>
        <div className="hand-fan">
          {playableCards.map((ci, idx) => {
            const cardKey = `${ci.card.suit}-${ci.card.rank}`;
            const isSelected = selectedCardKey === cardKey;
            const canPlayCard = ci.isPlayable && canPlay && game.phase === 'playing';
            return (
              <Card
                key={cardKey}
                suit={ci.card.suit}
                rank={ci.card.rank}
                size="lg"
                playable={canPlayCard}
                disabled={!ci.isPlayable || !canPlay || game.phase !== 'playing'}
                selected={isTouchDevice && isSelected}
                onClick={() => {
                  if (!canPlayCard) return;
                  if (isTouchDevice) {
                    if (isSelected) {
                      handlePlayCard(ci.card.suit, ci.card.rank);
                    } else {
                      setSelectedCardKey(cardKey);
                    }
                  } else {
                    handlePlayCard(ci.card.suit, ci.card.rank);
                  }
                }}
                className="hand-fan-card"
                style={
                  {
                    '--fan-index': idx,
                    '--fan-total': playableCards.length,
                  } as React.CSSProperties
                }
              />
            );
          })}
        </div>
      </div>

      {/* Round End */}
      {game.phase === 'roundEnd' && game.roundResults && !roundEndDismissed && (
        <div className="result-overlay">
          <div className="result-glass anim-slide-up-flex">
            <h2 className="result-title">Round {game.roundNumber} Complete</h2>
            <table className="round-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Call</th>
                  <th>Tricks</th>
                  <th>Score</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {game.roundResults.map((r) => {
                  const player = game.players.find((p) => p.id === r.playerId);
                  const met = r.tricksWon >= r.call;
                  return (
                    <tr key={r.playerId} className={met ? 'row-met' : 'row-missed'}>
                      <td>{player?.name ?? r.playerId}</td>
                      <td>{r.call}</td>
                      <td>{r.tricksWon}</td>
                      <td className={r.roundScore >= 0 ? 'score-pos' : 'score-neg'}>
                        {r.roundScore > 0 ? '+' : ''}
                        {r.roundScore}
                      </td>
                      <td>{r.totalScore}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <button className="btn-game btn-game-primary" onClick={handleContinueRound}>
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Match Abandoned */}
      {game.phase === 'abandoned' && (
        <div className="result-overlay">
          <div className="result-glass result-loss anim-slide-up-flex">
            <div className="result-trophy">🚪</div>
            <h2 className="result-title result-loss-title">Match Ended</h2>
            <p className="result-subtitle">All players have left the game.</p>
            <div className="result-actions">
              <button className="btn-game btn-game-primary" onClick={handleLeaveGame}>
                Back to Lobby
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Game Over */}
      {game.phase === 'gameOver' &&
        game.winnerId &&
        game.finalScores &&
        (game.winnerId === user?.id ? (
          /* ── WINNER VIEW ── */
          <div className="result-overlay result-overlay-win">
            <div className="result-glass result-win anim-celebrate">
              <div className="result-trophy result-trophy-win">🏆</div>
              <h2 className="result-title result-victory-title">Victory!</h2>
              <p className="result-subtitle">You won the game!</p>
              <div className="result-scores">
                {Object.entries(game.finalScores)
                  .sort(([, a], [, b]) => b - a)
                  .map(([pid, score], idx) => (
                    <div
                      key={pid}
                      className={`result-row ${pid === game.winnerId ? 'result-winner' : ''}`}
                    >
                      <span className="result-rank">#{idx + 1}</span>
                      <span className="result-name">
                        {game.players.find((p) => p.id === pid)?.name ?? pid}
                      </span>
                      <span className="result-pts">{score} pts</span>
                    </div>
                  ))}
              </div>
              <div className="result-actions">
                <button className="btn-game btn-game-primary" onClick={handleLeaveGame}>
                  Back to Lobby
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* ── LOSER VIEW ── */
          <div className="result-overlay">
            <div className="result-glass result-loss anim-slide-up-flex">
              <div className="result-trophy">🎮</div>
              <h2 className="result-title result-loss-title">
                {game.players.find((p) => p.id === game.winnerId)?.name} Wins
              </h2>
              <div className="result-scores">
                {Object.entries(game.finalScores)
                  .sort(([, a], [, b]) => b - a)
                  .map(([pid, score], idx) => (
                    <div
                      key={pid}
                      className={`result-row ${pid === game.winnerId ? 'result-winner' : ''}`}
                    >
                      <span className="result-rank">#{idx + 1}</span>
                      <span className="result-name">
                        {game.players.find((p) => p.id === pid)?.name ?? pid}
                      </span>
                      <span className="result-pts">{score} pts</span>
                    </div>
                  ))}
              </div>
              <div className="result-actions">
                <button className="btn-game btn-game-primary" onClick={handleLeaveGame}>
                  Back to Lobby
                </button>
              </div>
            </div>
          </div>
        ))}

      {/* Scoreboard overlay */}
      {showScoreboard && (
        <div className="result-overlay" onClick={() => setShowScoreboard(false)}>
          <div
            className="result-glass scoreboard-glass anim-slide-up-flex"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="scoreboard-header">
              <h2 className="result-title" style={{ margin: 0 }}>
                Score Table
              </h2>
              <button
                className="topbar-btn"
                onClick={() => setShowScoreboard(false)}
                aria-label="Close"
                style={{ flexShrink: 0 }}
              >
                ×
              </button>
            </div>
            <table className="round-table scoreboard-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th style={{ textAlign: 'left' }}>Player</th>
                  <th>Total</th>
                  <th>Call</th>
                  <th>Tricks</th>
                </tr>
              </thead>
              <tbody>
                {[...game.players]
                  .sort((a, b) => b.totalScore - a.totalScore)
                  .map((p, idx) => {
                    const isLocal = p.id === game.players[game.localPlayerIndex]?.id;
                    return (
                      <tr key={p.id} className={isLocal ? 'row-local' : ''}>
                        <td className="sb-rank">#{idx + 1}</td>
                        <td className="sb-name">
                          {p.isAI ? '🤖 ' : ''}
                          {p.name}
                          {isLocal && <span className="sb-you"> (You)</span>}
                          {!p.isConnected && (
                            <span className="disconnect-dot" style={{ marginLeft: 4 }} />
                          )}
                        </td>
                        <td className="sb-total score-pos">{p.totalScore}</td>
                        <td>{p.call ?? '—'}</td>
                        <td>{p.tricksWon}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            {game.roundHistory.length > 0 && (
              <>
                <p className="sb-history-label">Round History</p>
                <div className="sb-history">
                  {game.roundHistory.map((round, ri) => (
                    <div key={ri} className="sb-history-round">
                      <span className="sb-history-rnum">R{ri + 1}</span>
                      {round
                        .slice()
                        .sort((a, b) => b.roundScore - a.roundScore)
                        .map((r) => {
                          const pname =
                            game.players.find((p) => p.id === r.playerId)?.name ?? r.playerId;
                          return (
                            <span key={r.playerId} className="sb-history-entry">
                              {pname}: {r.roundScore > 0 ? '+' : ''}
                              {r.roundScore}
                            </span>
                          );
                        })}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Disconnected players banner */}
      {game.players.some((p) => !p.isConnected) && (
        <div className="disconnect-bar">
          {game.players
            .filter((p) => !p.isConnected)
            .map((p) => p.name)
            .join(', ')}{' '}
          disconnected
        </div>
      )}
    </div>
  );
}
